// VENDORED — do not edit here; change it in flashka-platform and copy it again.
//
// Source:  github.com/Flashka-ai/flashka-platform, packages/files-token/src/index.ts
// Commit:  cb86c4a8fb04fa5515dc4c09ba90b707e021efb0 (PR #344)
//
// Everything after the marker line below is that file byte for byte. It is
// excluded from Prettier and ESLint here so it stays identical; to check it:
//   sed '1,/^\/\/ ---- verbatim below ----$/d' shared/src/storage/files-token.ts \
//     | cmp - ../flashka-platform/packages/files-token/src/index.ts
// Why a copy and not a dependency: flashka-platform is a private repository
// that Vercel builds of this one cannot fetch, and the package is one pure
// file with no dependencies. The files worker (apps/files) verifies what this
// signs, so a change to the format lands there first.
// ---- verbatim below ----
/**
 * The files token: the analogue of an S3 presigned URL for the files worker
 * (apps/files) at files.flashka.ai.
 *
 * Whoever presigns today (flashka-web's server) signs a token with the shared
 * secret and hands the browser a URL; the worker verifies it and serves or
 * stores the one object it names. The token is
 *
 *   base64url(JSON payload) "." base64url(HMAC-SHA256(secret, first part))
 *
 * and the payload is `{ "v": 1, "op", "key", "exp", ...optional claims }`. The
 * MAC covers the encoded payload exactly as sent, so the JSON never has to be
 * canonical.
 *
 * PURE: Web Crypto and TextEncoder only, reached through `globalThis` with
 * local types, so this compiles with no DOM, node or Workers types in scope and
 * runs unchanged in the worker, in a browser and in node. Nothing here reads a
 * clock unless the caller leaves `now` out.
 */

/** Wire version. A verifier refuses any other. */
const FILES_TOKEN_VERSION = 1

/** The query parameter the token travels in. */
export const TOKEN_PARAM = "t"

/** The multipart query parameters: `?uploads` creates, `?uploadId=` names an upload, `&partNumber=` a part. */
export const UPLOADS_PARAM = "uploads"
export const UPLOAD_ID_PARAM = "uploadId"
export const PART_NUMBER_PARAM = "partNumber"

/**
 * The longest a token may live, in seconds: seven days, S3's own ceiling for a
 * presigned URL. `signFilesUrl` refuses more and the worker refuses a token
 * whose expiry is further away than this, so a signer that slips a unit cannot
 * mint a URL that works for years.
 */
export const MAX_TOKEN_LIFETIME_SECONDS = 7 * 24 * 60 * 60

/** A shorter secret is refused on both sides. `openssl rand -base64 32` is 44. */
const MIN_SECRET_LENGTH = 32

/** R2's limit on a key, in UTF-8 bytes. */
const MAX_KEY_BYTES = 1024

/**
 * Multipart rules. R2 requires every part but the last to be at least 5 MiB and
 * all of them the same size, and allows 10,000 parts. The flashka.ai zone is on
 * the Free plan, which caps a request body at 100 MB, so no single request (a
 * plain PUT or one part) may be larger than that; the decimal reading is the
 * smaller one and is the one used here.
 */
export const MIN_PART_BYTES = 5 * 1024 * 1024
export const MAX_PARTS = 10_000
export const MAX_REQUEST_BODY_BYTES = 100_000_000

/** Whether `n` is a part number R2 accepts: an integer from 1 to `MAX_PARTS`. */
export const isPartNumber = (n: unknown): n is number =>
  typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= MAX_PARTS

/**
 * The part size callers should use, and the size up to which a single PUT is
 * used instead. Half the zone's body cap: a part this size is well clear of the
 * limit, and a failed part costs at most 50 MiB to resend.
 */
export const RECOMMENDED_PART_BYTES = 50 * 1024 * 1024

export type FilesOp = "read" | "write"
export type FilesDisposition = "inline" | "attachment"

/**
 * What a token grants. `read` covers GET and HEAD of `key`; `write` covers a
 * single PUT of `key` and every multipart step for it, and nothing else.
 */
export interface FilesTokenClaims {
  op: FilesOp
  key: string
  /** Expiry, in Unix seconds. */
  exp: number
  /** write: the Content-Type the upload must declare (media type, compared without parameters). */
  contentType?: string
  /** write: the largest object the upload may produce, in bytes. */
  maxBytes?: number
  /** read: the filename a download is offered under. Implies `attachment` unless `disposition` says otherwise. */
  filename?: string
  /** read: the Content-Disposition type to answer with. */
  disposition?: FilesDisposition
}

export type FilesTokenFailure =
  /** Not two base64url parts, or a payload that is not a valid claim set. */
  | "malformed"
  /** The MAC does not match under any of the secrets given. */
  | "signature"
  | "expired"
  /** Expires further away than `maxLifetimeSeconds`. */
  | "lifetime"

export type FilesTokenResult =
  | { ok: true; claims: FilesTokenClaims }
  | { ok: false; reason: FilesTokenFailure }

/** Thrown for a caller's mistake: bad claims, a short secret, a bad origin. */
export class FilesTokenError extends Error {
  override name = "FilesTokenError"
}

// ---------------------------------------------------------------------------
// The runtime's Web Crypto and TextEncoder, typed locally. A global
// declaration here would clash with the DOM or Workers lib of whichever project
// compiles this source.

/** A Web Crypto `CryptoKey`, opaque here. */
type HmacKey = object

interface HmacSubtle {
  importKey(
    format: "raw",
    keyData: Uint8Array,
    algorithm: { name: "HMAC"; hash: "SHA-256" },
    extractable: false,
    usages: ["sign"],
  ): Promise<HmacKey>
  sign(algorithm: "HMAC", key: HmacKey, data: Uint8Array): Promise<ArrayBuffer>
}

interface Runtime {
  crypto: { subtle: HmacSubtle }
  TextEncoder: new () => { encode(input: string): Uint8Array }
  TextDecoder: new (label: "utf-8", options: { fatal: true }) => { decode(input: Uint8Array): string }
}

const runtime = globalThis as unknown as Runtime

const encoder = new runtime.TextEncoder()
const decoder = new runtime.TextDecoder("utf-8", { fatal: true })

const utf8 = (s: string): Uint8Array => encoder.encode(s)

const fromUtf8 = (bytes: Uint8Array): string | null => {
  try {
    return decoder.decode(bytes)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// base64url, by hand: `btoa` works on strings of Latin-1 code points and is
// not in every lib this compiles against. Exported for the tests.

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_"
const LOOKUP: Record<string, number> = Object.fromEntries([...ALPHABET].map((c, i) => [c, i]))

export function base64urlEncode(bytes: Uint8Array): string {
  let out = ""
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0)
    out += ALPHABET[(n >> 18) & 63]! + ALPHABET[(n >> 12) & 63]! + ALPHABET[(n >> 6) & 63]! + ALPHABET[n & 63]!
  }
  // A short last group padded with zero bytes: drop the characters that carry only padding.
  return out.slice(0, Math.ceil((bytes.length * 4) / 3))
}

/** `null` for anything that is not canonical unpadded base64url. */
export function base64urlDecode(text: string): Uint8Array | null {
  if (text.length % 4 === 1) return null
  const out = new Uint8Array(Math.floor((text.length * 3) / 4))
  let bits = 0
  let value = 0
  let at = 0
  for (const c of text) {
    const v = LOOKUP[c]
    if (v === undefined) return null
    value = (value << 6) | v
    bits += 6
    if (bits >= 8) {
      bits -= 8
      out[at++] = (value >> bits) & 255
    }
  }
  // Leftover bits must be zero, or two strings would decode to one byte string
  // and the encoding would not be canonical.
  if ((value & ((1 << bits) - 1)) !== 0) return null
  return out
}

// ---------------------------------------------------------------------------
// Keys and URLs

/**
 * Throws unless `key` can be an R2 key and survive a URL path. A `.` or `..`
 * segment cannot: the URL parser resolves dot segments, `%2e` spellings
 * included, so such a key would reach the worker as a different key. Exported
 * for the tests.
 */
export function assertKey(key: string): string {
  if (typeof key !== "string" || key.length === 0) throw new FilesTokenError("key must be a non-empty string")
  if (utf8(key).length > MAX_KEY_BYTES) throw new FilesTokenError(`key is over ${MAX_KEY_BYTES} bytes`)
  for (const segment of key.split("/")) {
    if (segment === "." || segment === "..") throw new FilesTokenError(`key has a "${segment}" segment`)
  }
  return key
}

/** The URL path a key is served at: each segment percent-encoded, `/` kept. */
export function encodeKeyPath(key: string): string {
  return "/" + assertKey(key).split("/").map(encodeURIComponent).join("/")
}

/**
 * The key a URL path names, or `null` for the root or a malformed escape. The
 * inverse of `encodeKeyPath` for every key it accepts.
 */
export function decodeKeyPath(pathname: string): string | null {
  if (!pathname.startsWith("/") || pathname.length === 1) return null
  try {
    return pathname.slice(1).split("/").map(decodeURIComponent).join("/")
  } catch {
    return null
  }
}

// Scheme, host and optional port; nothing after but an optional slash. Checked
// by pattern rather than with `URL`, which no ES lib declares.
const ORIGIN = /^https?:\/\/(?:[a-z0-9-]+(?:\.[a-z0-9-]+)*|\[[0-9a-f:.]+\])(?::\d{1,5})?\/?$/i

/** `https://files.flashka.ai`: lowercased, no trailing slash, path or query. */
function normaliseOrigin(origin: string): string {
  if (typeof origin !== "string" || !ORIGIN.test(origin)) {
    throw new FilesTokenError(`origin must be http(s)://host[:port] with no path, query or fragment: ${origin}`)
  }
  return origin.replace(/\/$/, "").toLowerCase()
}

export interface FilesUrlParts {
  /** The worker's origin: `https://files.flashka.ai` or `https://files-dev.flashka.ai`. */
  origin: string
  key: string
  token: string
  /** Multipart create: `POST <url>`. */
  uploads?: boolean
  /** Multipart part, complete or abort. */
  uploadId?: string
  /** Multipart part: `PUT <url>`, 1 to 10,000. */
  partNumber?: number
}

/**
 * The URL for one request against the worker. The same token goes on every
 * step of a multipart upload; only these parameters change.
 */
export function filesUrl(parts: FilesUrlParts): string {
  const params: string[] = []
  if (parts.uploads) params.push(UPLOADS_PARAM)
  if (parts.uploadId !== undefined) {
    if (parts.uploadId === "") throw new FilesTokenError("uploadId must not be empty")
    params.push(`${UPLOAD_ID_PARAM}=${encodeURIComponent(parts.uploadId)}`)
  }
  if (parts.partNumber !== undefined) {
    if (!isPartNumber(parts.partNumber)) throw new FilesTokenError(`partNumber must be an integer from 1 to ${MAX_PARTS}`)
    params.push(`${PART_NUMBER_PARAM}=${parts.partNumber}`)
  }
  params.push(`${TOKEN_PARAM}=${encodeURIComponent(parts.token)}`)
  return normaliseOrigin(parts.origin) + encodeKeyPath(parts.key) + "?" + params.join("&")
}

// ---------------------------------------------------------------------------
// Claims

const CLAIM_KEYS = new Set(["v", "op", "key", "exp", "contentType", "maxBytes", "filename", "disposition"])

const isPositiveInteger = (n: unknown): n is number => typeof n === "number" && Number.isSafeInteger(n) && n > 0

/**
 * The claims, checked, in a fixed order with no extra keys; `null` when they
 * are not a valid claim set. A field this version does not know makes the set
 * invalid rather than being ignored: a new field is a new restriction, and a
 * verifier that skipped it would grant more than the signer meant.
 */
function checkClaims(raw: unknown): FilesTokenClaims | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  for (const k of Object.keys(o)) if (!CLAIM_KEYS.has(k)) return null
  if (o.v !== FILES_TOKEN_VERSION) return null
  if (o.op !== "read" && o.op !== "write") return null
  if (typeof o.key !== "string") return null
  try {
    assertKey(o.key)
  } catch {
    return null
  }
  if (!isPositiveInteger(o.exp)) return null
  const claims: FilesTokenClaims = { op: o.op, key: o.key, exp: o.exp }
  if (o.contentType !== undefined) {
    if (o.op !== "write" || typeof o.contentType !== "string" || o.contentType.trim() === "") return null
    claims.contentType = o.contentType
  }
  if (o.maxBytes !== undefined) {
    if (o.op !== "write" || !isPositiveInteger(o.maxBytes)) return null
    claims.maxBytes = o.maxBytes
  }
  if (o.filename !== undefined) {
    if (o.op !== "read" || typeof o.filename !== "string" || o.filename === "") return null
    claims.filename = o.filename
  }
  if (o.disposition !== undefined) {
    if (o.op !== "read" || (o.disposition !== "inline" && o.disposition !== "attachment")) return null
    claims.disposition = o.disposition
  }
  return claims
}

// ---------------------------------------------------------------------------
// Signing

/** Imported keys by secret: a worker isolate verifies every request against the same one or two. */
const hmacKeys = new Map<string, Promise<HmacKey>>()

async function mac(secret: string, data: string): Promise<Uint8Array> {
  if (typeof secret !== "string" || secret.length < MIN_SECRET_LENGTH) {
    throw new FilesTokenError(`secret must be at least ${MIN_SECRET_LENGTH} characters`)
  }
  const subtle = runtime.crypto.subtle
  let key = hmacKeys.get(secret)
  if (!key) {
    key = subtle.importKey("raw", utf8(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
    hmacKeys.set(secret, key)
  }
  return new Uint8Array(await subtle.sign("HMAC", await key, utf8(data)))
}

/**
 * Compares every byte whatever the first difference, so the time taken says
 * nothing about how much of a forged MAC was right. Exported for the tests.
 */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!
  return diff === 0
}

/** Signs `claims` with `secret`. Throws `FilesTokenError` on an invalid claim set. */
export async function signFilesToken(claims: FilesTokenClaims, secret: string): Promise<string> {
  const checked = checkClaims({ v: FILES_TOKEN_VERSION, ...claims })
  if (!checked) throw new FilesTokenError("invalid claims")
  const payload = base64urlEncode(utf8(JSON.stringify({ v: FILES_TOKEN_VERSION, ...checked })))
  return `${payload}.${base64urlEncode(await mac(secret, payload))}`
}

export interface VerifyOptions {
  /** Unix seconds; defaults to the clock. */
  now?: number
  /** Defaults to `MAX_TOKEN_LIFETIME_SECONDS`. */
  maxLifetimeSeconds?: number
}

/**
 * Checks `token` against each of `secrets` in turn (the current key and,
 * during a rotation, the previous one) and returns its claims. It does not
 * check what the token is used FOR: the caller compares `op` and `key` with
 * the request.
 */
export async function verifyFilesToken(
  token: string,
  secrets: string | readonly string[],
  options: VerifyOptions = {},
): Promise<FilesTokenResult> {
  const keys = typeof secrets === "string" ? [secrets] : secrets
  if (keys.length === 0) throw new FilesTokenError("no secret to verify with")
  if (typeof token !== "string") return { ok: false, reason: "malformed" }
  const parts = token.split(".")
  if (parts.length !== 2 || parts[0] === "" || parts[1] === "") return { ok: false, reason: "malformed" }
  const [payload, signature] = parts as [string, string]
  const given = base64urlDecode(signature)
  const bytes = base64urlDecode(payload)
  if (!given || !bytes) return { ok: false, reason: "malformed" }

  let signed = false
  for (const secret of keys) {
    if (timingSafeEqual(await mac(secret, payload), given)) {
      signed = true
      break
    }
  }
  if (!signed) return { ok: false, reason: "signature" }

  // Parsed only once the MAC says the bytes are ours.
  const json = fromUtf8(bytes)
  if (json === null) return { ok: false, reason: "malformed" }
  let raw: unknown
  try {
    raw = JSON.parse(json)
  } catch {
    return { ok: false, reason: "malformed" }
  }
  const claims = checkClaims(raw)
  if (!claims) return { ok: false, reason: "malformed" }

  const now = options.now ?? Math.floor(Date.now() / 1000)
  if (now >= claims.exp) return { ok: false, reason: "expired" }
  if (claims.exp - now > (options.maxLifetimeSeconds ?? MAX_TOKEN_LIFETIME_SECONDS)) {
    return { ok: false, reason: "lifetime" }
  }
  return { ok: true, claims }
}

export interface SignFilesUrlOptions extends Omit<FilesTokenClaims, "exp"> {
  origin: string
  secret: string
  /** Seconds from `now`, at most `MAX_TOKEN_LIFETIME_SECONDS`. */
  expiresIn: number
  /** Unix seconds; defaults to the clock. */
  now?: number
}

/**
 * The drop-in for `getSignedUrl`: signs a token and returns the URL of the
 * object. For a write token, the multipart steps use `filesUrl` with the token
 * this URL carries (`new URL(url).searchParams.get(TOKEN_PARAM)`), or sign the
 * token once with `signFilesToken` and build every URL from it.
 */
export async function signFilesUrl(options: SignFilesUrlOptions): Promise<string> {
  const { origin, secret, expiresIn, now, ...rest } = options
  if (!isPositiveInteger(expiresIn) || expiresIn > MAX_TOKEN_LIFETIME_SECONDS) {
    throw new FilesTokenError(`expiresIn must be a whole number of seconds from 1 to ${MAX_TOKEN_LIFETIME_SECONDS}`)
  }
  const exp = (now ?? Math.floor(Date.now() / 1000)) + expiresIn
  const token = await signFilesToken({ ...rest, exp }, secret)
  return filesUrl({ origin, key: rest.key, token })
}

// ---------------------------------------------------------------------------
// Chunking

export interface PlannedPart {
  partNumber: number
  /** Byte offset in the file: `file.slice(offset, offset + length)`. */
  offset: number
  length: number
}

/**
 * Whether a file of `size` bytes goes up as a multipart upload rather than a
 * single PUT, at the given part size.
 */
export const useMultipart = (size: number, partBytes = RECOMMENDED_PART_BYTES): boolean => size > partBytes

/**
 * Cuts `size` bytes into parts R2 will accept: every part `partBytes` long but
 * the last, which is what is left. Throws if `partBytes` is outside R2's and the
 * zone's bounds or the file needs more than 10,000 parts.
 */
export function planParts(size: number, partBytes = RECOMMENDED_PART_BYTES): PlannedPart[] {
  if (!isPositiveInteger(size)) throw new FilesTokenError("size must be a positive integer")
  if (!Number.isSafeInteger(partBytes) || partBytes < MIN_PART_BYTES || partBytes > MAX_REQUEST_BODY_BYTES) {
    throw new FilesTokenError(`partBytes must be from ${MIN_PART_BYTES} to ${MAX_REQUEST_BODY_BYTES}`)
  }
  const count = Math.ceil(size / partBytes)
  if (count > MAX_PARTS) throw new FilesTokenError(`${count} parts is over R2's ${MAX_PARTS}; use larger parts`)
  const parts: PlannedPart[] = []
  for (let i = 0; i < count; i++) {
    const offset = i * partBytes
    parts.push({ partNumber: i + 1, offset, length: Math.min(partBytes, size - offset) })
  }
  return parts
}
